<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Категории товаров</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="kak-popast"
            class="typography__headline2"
          >
            Как попасть
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>Давайте заполним категории товаров вашего проекта.</p>

            <p>
              В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Товары&raquo;. Если вы&nbsp;еще не&nbsp;добавляли
              товары&nbsp;&mdash; на&nbsp;появившейся странице нажмите &laquo;Настроить категории&raquo;.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/kategorii-tovarov-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            Если вы&nbsp;уже добавляли товары&nbsp;&mdash; на&nbsp;появившейся странице нажмите &laquo;Управление
            категориями&raquo;.
          </p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/kategorii-tovarov-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="dobavleniye-i-prosmotr-kategoriij"
            class="typography__headline2"
          >
            Добавление и просмотр категорий
          </FaqArticlesBlockHeading>

          <p>
            Если вы&nbsp;еще не&nbsp;добавляли категории&nbsp;&mdash; на&nbsp;появившейся странице нажмите
            &laquo;Добавить категорию&raquo;.
          </p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/kategorii-tovarov-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>При добавлении первой категории можно добавить её&nbsp;только как главную категорию.</p>

            <p>Ниже нужно заполнить название и&nbsp;обложку.</p>

            <p>
              После заполнения всей информации нажмите &laquo;Добавить&raquo; в&nbsp;самом низу окна. После успешного
              добавления увидите в&nbsp;правом верхнем углу уведомление.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/kategorii-tovarov-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>
              Когда уже есть добавленные товары&nbsp;&mdash; можно выбрать фильтры для нужных категорий через
              их&nbsp;редактирование или добавление новой.
            </p>

            <p>
              Фильтры выбираются по&nbsp;параметрам ваших товаров. В&nbsp;каталоге клиенту будет доступна фильтрация
              по&nbsp;ним.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/kategorii-tovarov-5.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>Если категории добавляли&nbsp;&mdash; попадете на&nbsp;их&nbsp;страницу.</p>

            <p>Сверху справа можно добавить новую категорию, нажав соответствующую кнопку.</p>

            <p>
              В&nbsp;списке есть важная информация о&nbsp;категориях, возможность быстро скрыть её&nbsp;с&nbsp;сайта,
              добавить её&nbsp;подкатегорию, перейти к&nbsp;редактированию или удалению.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/kategorii-tovarov-6.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>
              При добавлении очередной категории можно добавить её&nbsp;как главную категорию или как подкатегорию,
              выбрав где она будет находиться.
            </p>

            <p>Если выберите её&nbsp;подкатегорией, ниже нужно будет заполнить только название.</p>

            <p>
              После заполнения всей информации нажмите &laquo;Добавить&raquo; в&nbsp;самом низу окна. После успешного
              добавления увидите в&nbsp;правом верхнем углу уведомление.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/kategorii-tovarov-7.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            После добавления подкатегории, в&nbsp;списке можно будет раскрыть иерархию категорий товаров, нажав
            на&nbsp;главную категорию со&nbsp;стрелочкой слева.
          </p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/kategorii-tovarov-8.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С категориями разобрались. Самое время перейти к
            <BaseLink
              class="ui-link-secondary"
              to="/faq/articles/dobavlenie-i-redaktirovanie-tovara"
              >добавлению товаров</BaseLink
            >.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Заказы с товарами",
    url: "/faq/categories/zakazy-s-tovarami",
  },
  {
    name: "Категории товаров",
  },
];

const menu = [
  {
    name: "Как&nbsp;попасть",
    url: "kak-popast",
  },
  {
    name: "Добавление&nbsp;и&nbsp;просмотр категорий",
    url: "dobavleniye-i-prosmotr-kategoriij",
  },
];

const relatedArticles = [
  {
    name: "Способы доставки",
    slug: "sposoby-dostavki",
  },
  {
    name: "Добавление и&nbsp;редактирование товара",
    slug: "dobavlenie-i-redaktirovanie-tovara",
  },
  {
    name: "Список товаров и&nbsp;их данные",
    slug: "spisok-tovarov-i-ikh-dannye",
  },
  {
    name: "Цифровые товары",
    slug: "tsifrovye-tovary",
  },
  {
    name: "Список заказов и&nbsp;их данные",
    slug: "spisok-zakazov-i-ikh-dannye",
  },
  {
    name: "Добавление и&nbsp;редактирование заказа",
    slug: "dobavlenie-i-redaktirovanie-zakaza",
  },
];

useSeoMeta({
  title: "Категории товаров",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
